import { supabase } from './supabase';
import {
  AP_BIO_WORKFLOW_DATA,
  AP_BIO_WORKFLOW_FRQ,
  AP_BIO_WORKFLOW_LEARNING,
  AP_BIO_WORKFLOW_REVISION,
} from './apBioWorkflows';

export const AP_BIO_ONBOARDING_VERSION = 'diagnostic_v3';
export const AP_BIO_ONBOARDING_CONTEXT_STORAGE_KEY = 'apbio_onboarding_context';
export const AP_BIO_ONBOARDING_FIRST_PROMPT_KEY = 'apbio_onboarding_first_prompt_logged';
export const AP_BIO_ONBOARDING_PROGRESS_STORAGE_KEY = 'apbio_onboarding_progress';
export const AP_BIO_ONBOARDING_GUIDE_SEEN_STORAGE_KEY = 'apbio_ai_tutor_onboarding_guide_seen_v1';
export const AP_BIO_FLASHCARD_POINTERS_SEEN_STORAGE_KEY = 'apbio_flashcards_onboarding_pointers_seen_v1';

export const AP_BIO_PAIN_POINT_FRQ = 'frq';
export const AP_BIO_PAIN_POINT_DATA = 'data';
export const AP_BIO_PAIN_POINT_STUDY = 'study';

export const AP_BIO_MODE_LEARNING = 'learning_mode';
export const AP_BIO_MODE_MARK_SCHEME = 'mark_scheme_mode';
export const AP_BIO_MODE_BOTH = 'both_modes';

export const AP_BIO_EXAM_STATS = [
  {
    value: '16.8%',
    label: 'Scored a 5 in 2024.',
    detail: 'They are not smarter than you. They answer differently.',
  },
  {
    value: '< 50%',
    label: 'Average FRQ score, every year.',
    detail: 'Students know the content. They describe it. The rubric requires them to explain it.',
  },
  {
    value: '2%',
    label: 'Earned the hardest FRQ point in 2025.',
    detail: 'Not impossible. Just untaught. The Chief Reader named it. No one acted on it.',
  },
];

export const AP_BIO_CHIEF_READER_QUOTE = {
  text: '"Students describe what happened. They do not explain the biology. The rubric requires a mechanism — and year after year, the majority of students do not know what that means."',
  attribution: 'Chief Reader Reports, 2013–2025',
};

export const AP_BIO_PAIN_STAT_CALLOUTS = {
  [AP_BIO_PAIN_POINT_FRQ]: 'The 2022 Chief Reader: "Students describe what happened, but do not explain the biology clearly enough to earn the point." Every year, the same note.',
  [AP_BIO_PAIN_POINT_DATA]: 'Only 6% of students earned full marks on data-analysis questions. This is not a talent gap — it is a method gap.',
  [AP_BIO_PAIN_POINT_STUDY]: 'AP Biology rewards transfer, not recall. Students who know the chapter often freeze when the question changes shape.',
};

export const AP_BIO_DIAGNOSIS_CONTENT = {
  [AP_BIO_PAIN_POINT_FRQ]: {
    heading: 'You are not far off. The rubric is the missing piece.',
    supportLine: 'Built on every official AP Biology FRQ, rubric, and Chief Reader pattern from 2013–2025.',
    evidence: [
      {
        title: 'Read the command word',
        detail: 'Different verbs ask for different moves, and treating them the same costs points.',
      },
      {
        title: 'Write the mechanism',
        detail: 'Name the biological cause, not just the visible outcome.',
      },
      {
        title: 'Stop after the point',
        detail: 'Extra lines can turn a correct answer into a contradicted one.',
      },
    ],
    pattern: 'You will learn what the prompt wants, say the mechanism clearly, and stop once the point is earned.',
  },
  [AP_BIO_PAIN_POINT_DATA]: {
    heading: 'This is not guesswork. There is a method for it.',
    supportLine: 'Built on official AP Biology data questions, scoring guides, and lab-style experiment prompts.',
    evidence: [
      {
        title: 'Find the setup first',
        detail: 'Start by naming the independent variable, dependent variable, and control.',
      },
      {
        title: 'Connect trend to mechanism',
        detail: 'Say what changed, then link that change to the biology causing it.',
      },
      {
        title: 'State only what data supports',
        detail: 'Separate what the graph shows from what you are inferring beyond it.',
      },
    ],
    pattern: 'You will use the same three-step method on every graph and experiment question.',
  },
  [AP_BIO_PAIN_POINT_STUDY]: {
    heading: 'You do not need more facts. You need a way to use them.',
    supportLine: 'Built on the AP Biology course framework, released questions, and scoring reports that show what the exam rewards.',
    evidence: [
      {
        title: 'Learn the big links',
        detail: 'Strong unit connections make the course feel smaller and questions feel less random.',
      },
      {
        title: 'Apply before you review',
        detail: 'Use the idea on a question first, then fill the gap you actually exposed.',
      },
      {
        title: 'Train for transfer',
        detail: 'Practice with new scenarios so the biology stays usable when the question changes.',
      },
    ],
    pattern: 'You will connect the core ideas, then practice using them on unfamiliar AP Biology questions.',
  },
};

export const AP_BIO_TUTOR_MODES_SHOWCASE = [
  { name: 'Learning Breakdown', description: 'Step-by-step concept mastery.' },
  { name: 'FRQ Grader', description: 'Marks your answers like an AP reader.' },
  { name: 'Data & Experiments', description: 'Walks through graphs and controls.' },
  { name: 'Practice Engine', description: 'Fresh, targeted AP MCQs and FRQs.' },
  { name: 'Study Planner', description: 'Turns weak topics into a study plan.' },
];

export const AP_BIO_TRAINING_CREDENTIALS = [
  {
    label: 'Every AP Biology FRQ and rubric, 2013–2025',
    detail: '12 years of real exam questions and their exact scoring criteria. Every point that was ever awarded or denied.',
  },
  {
    label: 'All College Board MCQ releases',
    detail: '30 official multiple-choice sets from the AP Bio Course and Exam Description — the same document your teacher uses to plan the year.',
  },
  {
    label: 'Chief Reader Reports, 2013–2025',
    detail: 'The documents where the head examiner explains, in plain language, what cost students points and why. Thirteen years of patterns.',
  },
  {
    label: 'The full Course and Exam Description',
    detail: 'Every learning objective, science practice, and content boundary College Board defines. The complete scope of what can be tested.',
  },
  {
    label: 'Lab practicals and scoring examples',
    detail: 'Real investigations with scoring guidelines — the experimental design questions that appear every year and trip up the same students.',
  },
];

export const AP_BIO_MODE_SUBHEADINGS = {
  [AP_BIO_PAIN_POINT_FRQ]: 'How do you want to work on your FRQ answers?',
  [AP_BIO_PAIN_POINT_DATA]: 'How do you want to approach data and experiment questions?',
  [AP_BIO_PAIN_POINT_STUDY]: 'How do you want to turn content into usable exam skill?',
};

export const AP_BIO_PAIN_POINT_OPTIONS = [
  {
    id: AP_BIO_PAIN_POINT_FRQ,
    title: 'I write full answers and still lose FRQ points',
    description: 'The biology is there. Something about how I am writing it is not earning credit — and I cannot see what.',
  },
  {
    id: AP_BIO_PAIN_POINT_DATA,
    title: 'Data questions and experiments feel like guesswork',
    description: 'Unfamiliar graphs, unknown setups, experiment design — I do not have a process that works every time.',
  },
  {
    id: AP_BIO_PAIN_POINT_STUDY,
    title: 'There is too much content, and I do not know how to apply it',
    description: 'I can review the material, but on unfamiliar questions I freeze, miss the connection, or do not know how to use what I know.',
  },
];

export const AP_BIO_MODE_OPTIONS = [
  {
    id: AP_BIO_MODE_LEARNING,
    title: 'Understand the biology deeply',
    eyebrow: 'Build Understanding',
    description: 'Start from first principles. Build the understanding that makes every question — even unfamiliar ones — feel grounded.',
  },
  {
    id: AP_BIO_MODE_MARK_SCHEME,
    title: 'Write answers that earn points',
    eyebrow: 'Train Your Answers',
    description: 'Learn the exact language the rubric rewards. Train your responses to give AP readers what they are looking for.',
  },
  {
    id: AP_BIO_MODE_BOTH,
    title: 'Both',
    eyebrow: 'Recommended',
    description: 'Understand the biology and write it like an AP reader expects. The complete path from knowledge to score.',
  },
];

const VALID_PAIN_POINTS = new Set(AP_BIO_PAIN_POINT_OPTIONS.map((option) => option.id));
const VALID_MODES = new Set(AP_BIO_MODE_OPTIONS.map((option) => option.id));
const ONBOARDING_TOTAL_STEPS = 7;

export const AP_BIO_MODE_PREVIEW = {
  question: 'Claim: Lower enzyme activity reduces ATP production. Explain why.',
  answers: {
    [AP_BIO_MODE_LEARNING]: 'Less enzyme activity slows the pathway, so fewer products reach respiration. That means a weaker proton gradient and less ATP.',
    [AP_BIO_MODE_MARK_SCHEME]: 'Reduced enzyme activity drops pathway flux. Less substrate enters respiration, decreasing electron transport and ATP production.',
    [AP_BIO_MODE_BOTH]: 'First we understand the bottleneck, then we translate it into the exact mechanism the rubric requires for full credit.',
  },
};

const MEMBERSHIP_COPY = {
  [AP_BIO_PAIN_POINT_FRQ]: {
    eyebrow: 'FRQ training',
    title: 'Your FRQ answers are about to start earning what they deserve.',
    subtitle: 'Line-by-line rubric feedback built from 12 years of real AP Biology scoring. The same logic your AP reader uses — applied to your answers.',
    bullets: [
      'Trained on every FRQ and rubric from 2013–2025 — not internet content.',
      'Chief Reader warning patterns are woven into FRQ coaching and answer rewrites.',
      'Explains biology at any level — from foundations to exam-day precision.',
    ],
  },
  [AP_BIO_PAIN_POINT_DATA]: {
    eyebrow: 'Data & experiment training',
    title: 'Data questions will stop feeling like guesswork.',
    subtitle: 'A repeatable process for every graph, control, and conclusion — trained on every AP Biology data question ever officially released.',
    bullets: [
      'Trained on every FRQ and rubric from 2013–2025 — not internet content.',
      'Chief Reader warning patterns are woven into FRQ coaching and answer rewrites.',
      'Explains biology at any level — from foundations to exam-day precision.',
    ],
  },
  [AP_BIO_PAIN_POINT_STUDY]: {
    eyebrow: 'Content to application',
    title: 'The content is about to feel more connected, and far more usable.',
    subtitle: 'APDoneRight helps you stop treating AP Biology like a pile of facts and start using it the way the exam actually demands.',
    bullets: [
      'Trained on every FRQ and rubric from 2013–2025 — not internet content.',
      'Chief Reader warning patterns are woven into FRQ coaching and answer rewrites.',
      'Explains biology at any level — from foundations to exam-day precision.',
    ],
  },
};

const TRIAL_BRIDGE_OUTCOMES = {
  [AP_BIO_PAIN_POINT_FRQ]: {
    [AP_BIO_MODE_LEARNING]: [
      'Start with one FRQ and unpack the biology the prompt is really testing.',
      'Learn how command words change what a strong answer needs.',
    ],
    [AP_BIO_MODE_MARK_SCHEME]: [
      'Start with one FRQ and compare your wording with point-earning language.',
      'See where a brief, specific answer scores better than a longer one.',
    ],
    [AP_BIO_MODE_BOTH]: [
      'Start with one FRQ and strengthen both the biology and the wording.',
      'See how a full-credit answer is built step by step.',
    ],
  },
  [AP_BIO_PAIN_POINT_DATA]: {
    [AP_BIO_MODE_LEARNING]: [
      'Start with one graph or experiment and identify variables, controls, and trends.',
      'Practice connecting the data to the biology behind it.',
    ],
    [AP_BIO_MODE_MARK_SCHEME]: [
      'Turn one data set into a supported conclusion in clear AP Biology language.',
      'Practice saying what the data supports and what it does not support.',
    ],
    [AP_BIO_MODE_BOTH]: [
      'Start with one unfamiliar experiment and work through both the science and the answer.',
      'Build a repeatable way to handle graph and experiment questions.',
    ],
  },
  [AP_BIO_PAIN_POINT_STUDY]: {
    [AP_BIO_MODE_LEARNING]: [
      'Start with the concepts that unlock the most downstream AP Biology questions.',
      'Turn overloaded content into a smaller set of connected ideas you can actually use.',
    ],
    [AP_BIO_MODE_MARK_SCHEME]: [
      'Practice turning biology you understand into precise, point-earning exam language.',
      'See how application questions reward mechanism and connection, not chapter recall.',
    ],
    [AP_BIO_MODE_BOTH]: [
      'Rebuild the highest-value concepts, then apply them on the kinds of questions AP Bio actually asks.',
      'Make your study sessions feel less like cramming and more like real exam preparation.',
    ],
  },
};

const STARTER_PROMPTS = {
  [AP_BIO_PAIN_POINT_FRQ]: [
    {
      label: 'Grade my FRQ like an AP reader',
      prompt: 'Grade my FRQ like an AP reader and show me exactly which justification points I earned or missed.',
      workflow: AP_BIO_WORKFLOW_FRQ,
    },
    {
      label: 'Why did this FRQ lose points?',
      prompt: 'Why would this FRQ answer lose points even if the biology is mostly right?',
      workflow: AP_BIO_WORKFLOW_FRQ,
    },
    {
      label: 'Rewrite this for full credit',
      prompt: 'Rewrite this AP Bio FRQ answer so it is more specific, directional, and likely to earn full credit.',
      workflow: AP_BIO_WORKFLOW_FRQ,
    },
  ],
  [AP_BIO_PAIN_POINT_DATA]: [
    {
      label: 'Walk me through this graph',
      prompt: 'Walk me through this graph or experiment step by step and explain what the data means biologically.',
      workflow: AP_BIO_WORKFLOW_DATA,
    },
    {
      label: 'Find the variables and controls',
      prompt: 'Help me identify the independent variable, dependent variable, controls, and what would count as strong evidence here.',
      workflow: AP_BIO_WORKFLOW_DATA,
    },
    {
      label: 'What conclusion is supported?',
      prompt: 'What conclusion is actually supported by this AP Bio data, and what would not be justified?',
      workflow: AP_BIO_WORKFLOW_DATA,
    },
  ],
  [AP_BIO_PAIN_POINT_STUDY]: [
    {
      label: 'Help me apply what I know',
      prompt: 'Help me apply AP Bio content on unfamiliar questions instead of just reviewing facts.',
      workflow: AP_BIO_WORKFLOW_LEARNING,
    },
    {
      label: 'Show me the big connections',
      prompt: 'Show me the big AP Bio concept connections so the course feels less like isolated facts and more like a system.',
      workflow: AP_BIO_WORKFLOW_REVISION,
    },
    {
      label: 'Why can I not use the content?',
      prompt: 'Why does AP Biology feel harder to apply than to memorize, and how should I train that skill?',
      workflow: AP_BIO_WORKFLOW_LEARNING,
    },
  ],
};

export function normalizeApBioOnboardingContext(value) {
  if (!value || typeof value !== 'object') return null;

  const painPoint = String(value.painPoint || '').trim().toLowerCase();
  const mode = String(value.mode || '').trim().toLowerCase();

  if (!VALID_PAIN_POINTS.has(painPoint) || !VALID_MODES.has(mode)) {
    return null;
  }

  return {
    painPoint,
    mode,
    version: value.version || AP_BIO_ONBOARDING_VERSION,
  };
}

export function buildApBioOnboardingContext({ painPoint, mode } = {}) {
  return normalizeApBioOnboardingContext({
    painPoint,
    mode,
    version: AP_BIO_ONBOARDING_VERSION,
  });
}

export function normalizeApBioOnboardingProgress(value) {
  if (!value || typeof value !== 'object') return null;

  const stepNumber = Number(value.step);
  const normalizedStep = Number.isFinite(stepNumber)
    ? Math.min(ONBOARDING_TOTAL_STEPS, Math.max(1, Math.round(stepNumber)))
    : 1;
  const painPoint = VALID_PAIN_POINTS.has(String(value.painPoint || '').trim().toLowerCase())
    ? String(value.painPoint || '').trim().toLowerCase()
    : '';
  const mode = VALID_MODES.has(String(value.mode || '').trim().toLowerCase())
    ? String(value.mode || '').trim().toLowerCase()
    : '';

  let step = normalizedStep;

  if (!painPoint && step > 2) {
    step = 2;
  }

  if (!mode && step > 4) {
    step = 4;
  }

  return {
    step,
    painPoint,
    mode,
    version: value.version || AP_BIO_ONBOARDING_VERSION,
  };
}

export function getApBioPainPointMeta(painPoint) {
  return AP_BIO_PAIN_POINT_OPTIONS.find((option) => option.id === painPoint) || AP_BIO_PAIN_POINT_OPTIONS[0];
}

export function getApBioModeMeta(mode) {
  return AP_BIO_MODE_OPTIONS.find((option) => option.id === mode) || AP_BIO_MODE_OPTIONS[0];
}

export function getApBioModePreviewAnswer(mode) {
  return AP_BIO_MODE_PREVIEW.answers[mode] || AP_BIO_MODE_PREVIEW.answers[AP_BIO_MODE_LEARNING];
}

export function getApBioTrialBridgeOutcomes(context) {
  const normalized = normalizeApBioOnboardingContext(context);
  if (!normalized) return TRIAL_BRIDGE_OUTCOMES[AP_BIO_PAIN_POINT_STUDY][AP_BIO_MODE_BOTH];
  return TRIAL_BRIDGE_OUTCOMES[normalized.painPoint][normalized.mode];
}

export function getApBioMembershipCopy(context) {
  const normalized = normalizeApBioOnboardingContext(context);
  if (!normalized) return MEMBERSHIP_COPY[AP_BIO_PAIN_POINT_STUDY];
  return MEMBERSHIP_COPY[normalized.painPoint] || MEMBERSHIP_COPY[AP_BIO_PAIN_POINT_STUDY];
}

export function getApBioPreferredWorkflow(context) {
  const normalized = normalizeApBioOnboardingContext(context);
  if (!normalized) return AP_BIO_WORKFLOW_LEARNING;
  if (normalized.mode === AP_BIO_MODE_MARK_SCHEME) return AP_BIO_WORKFLOW_FRQ;
  if (normalized.painPoint === AP_BIO_PAIN_POINT_DATA) return AP_BIO_WORKFLOW_DATA;
  if (normalized.painPoint === AP_BIO_PAIN_POINT_STUDY) return AP_BIO_WORKFLOW_LEARNING;
  return AP_BIO_WORKFLOW_LEARNING;
}

export function getApBioStarterPrompts(context) {
  const normalized = normalizeApBioOnboardingContext(context);
  if (!normalized) return STARTER_PROMPTS[AP_BIO_PAIN_POINT_STUDY];
  return STARTER_PROMPTS[normalized.painPoint] || STARTER_PROMPTS[AP_BIO_PAIN_POINT_STUDY];
}

// --- User-scoped storage helpers ---
// Keys are scoped by userId so switching accounts never leaks progress.

function scopedKey(baseKey, userId) {
  if (!userId) return null;
  return `${baseKey}:${userId}`;
}

function writeScoped(baseKey, userId, serialized) {
  const key = scopedKey(baseKey, userId);
  if (!key) return;
  try { sessionStorage.setItem(key, serialized); } catch (_) { /* non-blocking */ }
  try { localStorage.setItem(key, serialized); } catch (_) { /* non-blocking */ }
}

function readScoped(baseKey, userId) {
  if (!userId) return [];
  const key = scopedKey(baseKey, userId);
  const sources = [];
  try { sources.push(sessionStorage.getItem(key)); } catch (_) { /* ignore */ }
  try { sources.push(localStorage.getItem(key)); } catch (_) { /* ignore */ }
  return sources;
}

function removeScoped(baseKey, userId) {
  const key = scopedKey(baseKey, userId);
  if (!key) return;
  try { sessionStorage.removeItem(key); } catch (_) { /* non-blocking */ }
  try { localStorage.removeItem(key); } catch (_) { /* non-blocking */ }
  // Also clean up legacy unscoped key
  if (userId) {
    try { sessionStorage.removeItem(baseKey); } catch (_) { /* non-blocking */ }
    try { localStorage.removeItem(baseKey); } catch (_) { /* non-blocking */ }
  }
}

function hasScopedBoolean(baseKey, userId = '') {
  const key = scopedKey(baseKey, userId);
  if (!key) return false;
  try {
    return localStorage.getItem(key) === 'true';
  } catch (_) {
    return false;
  }
}

function markScopedBoolean(baseKey, userId = '') {
  const key = scopedKey(baseKey, userId);
  if (!key) return;
  try {
    localStorage.setItem(key, 'true');
  } catch (_) {
    // Non-blocking.
  }
}

export function persistApBioOnboardingContext(context, userId = '') {
  const normalized = normalizeApBioOnboardingContext(context);
  if (!normalized) return;
  writeScoped(AP_BIO_ONBOARDING_CONTEXT_STORAGE_KEY, userId, JSON.stringify(normalized));
}

export function persistApBioOnboardingProgress(progress, userId = '') {
  const normalized = normalizeApBioOnboardingProgress(progress);
  if (!normalized) return;
  writeScoped(AP_BIO_ONBOARDING_PROGRESS_STORAGE_KEY, userId, JSON.stringify(normalized));
}

export function readStoredApBioOnboardingProgress(userId = '') {
  const sources = readScoped(AP_BIO_ONBOARDING_PROGRESS_STORAGE_KEY, userId);

  for (const source of sources) {
    if (!source) continue;
    try {
      const parsed = JSON.parse(source);
      const normalized = normalizeApBioOnboardingProgress(parsed);
      if (normalized) return normalized;
    } catch (_) {
      // keep trying
    }
  }

  return null;
}

export function clearStoredApBioOnboardingProgress(userId = '') {
  removeScoped(AP_BIO_ONBOARDING_PROGRESS_STORAGE_KEY, userId);
}

export function readStoredApBioOnboardingContext(userId = '') {
  const sources = readScoped(AP_BIO_ONBOARDING_CONTEXT_STORAGE_KEY, userId);

  for (const source of sources) {
    if (!source) continue;
    try {
      const parsed = JSON.parse(source);
      const normalized = normalizeApBioOnboardingContext(parsed);
      if (normalized) return normalized;
    } catch (_) {
      // keep trying
    }
  }

  return null;
}

/**
 * Clear all onboarding storage for a user. Call on sign-out.
 */
export function clearAllApBioOnboardingStorage(userId = '') {
  removeScoped(AP_BIO_ONBOARDING_PROGRESS_STORAGE_KEY, userId);
  removeScoped(AP_BIO_ONBOARDING_CONTEXT_STORAGE_KEY, userId);
}

export function hasSeenApBioOnboardingGuide(userId = '') {
  return hasScopedBoolean(AP_BIO_ONBOARDING_GUIDE_SEEN_STORAGE_KEY, userId);
}

export function markApBioOnboardingGuideSeen(userId = '') {
  markScopedBoolean(AP_BIO_ONBOARDING_GUIDE_SEEN_STORAGE_KEY, userId);
}

export function clearApBioOnboardingGuideSeen(userId = '') {
  removeScoped(AP_BIO_ONBOARDING_GUIDE_SEEN_STORAGE_KEY, userId);
}

export function hasSeenFlashcardOnboardingPointers(userId = '') {
  return hasScopedBoolean(AP_BIO_FLASHCARD_POINTERS_SEEN_STORAGE_KEY, userId);
}

export function markFlashcardOnboardingPointersSeen(userId = '') {
  markScopedBoolean(AP_BIO_FLASHCARD_POINTERS_SEEN_STORAGE_KEY, userId);
}

export function clearFlashcardOnboardingPointersSeen(userId = '') {
  removeScoped(AP_BIO_FLASHCARD_POINTERS_SEEN_STORAGE_KEY, userId);
}

export function clearApBioOnboardingFirstPromptLogged() {
  try {
    sessionStorage.removeItem(AP_BIO_ONBOARDING_FIRST_PROMPT_KEY);
  } catch (_) {
    // Non-blocking.
  }
}

export function clearApBioOnboardingUxState(userId = '') {
  clearApBioOnboardingGuideSeen(userId);
  clearFlashcardOnboardingPointersSeen(userId);
  clearApBioOnboardingFirstPromptLogged();
}

export function markApBioFirstPromptLogged() {
  try {
    sessionStorage.setItem(AP_BIO_ONBOARDING_FIRST_PROMPT_KEY, 'true');
  } catch (error) {
    // Non-blocking.
  }
}

export function hasLoggedApBioFirstPrompt() {
  try {
    return sessionStorage.getItem(AP_BIO_ONBOARDING_FIRST_PROMPT_KEY) === 'true';
  } catch (error) {
    return false;
  }
}

export async function logApBioOnboardingEvent({
  userId,
  eventName,
  step = null,
  metadata = {},
  client = supabase,
} = {}) {
  if (!userId || !eventName || !client?.from) return;

  try {
    await client
      .from('onboarding_events')
      .insert({
        user_id: userId,
        event_name: eventName,
        step,
        metadata: metadata && typeof metadata === 'object' ? metadata : {},
      });
  } catch (error) {
    // Telemetry must never block UX.
  }
}
